import { createContext, useCallback, useContext, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { memberKeys, useInvalidateMembers, useMembersQuery } from '@/api/query/user';
import { useAppContext } from '@/contexts/AppContext';
import type { Member } from '@/types';

interface MemberContextType {
  members: Member[];
  isLoading: boolean;
  isFetching: boolean;
  error: string | null;
  addMember: (member: Member) => void;
  updateMember: (id: string, member: Partial<Member>) => void;
  deleteMember: (id: string) => void;
  getMemberById: (id: string) => Member | undefined;
  refreshMembers: () => Promise<void>;
}

const MemberContext = createContext<MemberContextType | undefined>(undefined);

export function MemberProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAppContext();
  const queryClient = useQueryClient();
  const invalidateMembers = useInvalidateMembers();

  const { data, isLoading, isFetching, isError, error } = useMembersQuery(isAuthenticated);

  const members = isAuthenticated ? (data ?? []) : [];

  const refreshMembers = useCallback(async () => {
    await invalidateMembers();
  }, [invalidateMembers]);

  const addMember = useCallback(
    (member: Member) => {
      queryClient.setQueryData<Member[]>(memberKeys.list(), (prev = []) => [
        member,
        ...prev.filter((item) => item.id !== member.id),
      ]);
    },
    [queryClient]
  );

  const updateMember = useCallback(
    (id: string, updatedMember: Partial<Member>) => {
      queryClient.setQueryData<Member[]>(memberKeys.list(), (prev = []) =>
        prev.map((member) => (member.id === id ? { ...member, ...updatedMember } : member))
      );
    },
    [queryClient]
  );

  const deleteMember = useCallback(
    (id: string) => {
      queryClient.setQueryData<Member[]>(memberKeys.list(), (prev = []) =>
        prev.filter((member) => member.id !== id)
      );
    },
    [queryClient]
  );

  const getMemberById = useCallback(
    (id: string) => members.find((member) => member.id === id),
    [members]
  );

  const contextValue: MemberContextType = {
    members,
    isLoading: isAuthenticated && isLoading,
    isFetching: isAuthenticated && isFetching,
    error: isError
      ? error instanceof Error
        ? error.message
        : 'Unable to load members. Please try again.'
      : null,
    addMember,
    updateMember,
    deleteMember,
    getMemberById,
    refreshMembers,
  };

  return <MemberContext.Provider value={contextValue}>{children}</MemberContext.Provider>;
}

export function useMemberContext() {
  const context = useContext(MemberContext);
  if (context === undefined) {
    throw new Error('useMemberContext must be used within a MemberProvider');
  }
  return context;
}
